import { BrowserRouter, Outlet, Route, Routes, Navigate } from 'react-router';
import { Auth0Provider, withAuthenticationRequired } from '@auth0/auth0-react';

import env from 'env';

import { DefaultPage, BookingFlow } from 'layouts';
import { RootStore, BookingStore } from 'store';
import {
  // Common
  DashboardPage,

  // Tasks
  CallbacksPage,
  TicketsPage,

  // Booking
  StartPage,
  BookingLocationPage,
  BookingTechnicianPage,
  BookingPaymentPage,
  BookingConfirmationPage,
  PartsOnlyJobPage,

  // Job
  JobSearchPage,
  JobReschedulePage,
  JobCancelPage,
  JobEditPage,

  // Customer
  CustomerCreatePage,
  CustomerSearchPage,
  CustomerEditPage,

  // Technician
  TechnicianSearchPage,
  TechnicianEditPage,
  TechnicianAwayStatusPage,
  TechnicianRecurringAvailabilityPage,
} from 'pages';

const App = () => {
  const { resetAppData } = RootStore;
  const { resetBookingProgress } = BookingStore;
  const ProtectedRoute = ({ page, ...args }: any) => {
    const Page = withAuthenticationRequired(page, args);
    return <Page />;
  };

  return (
    <BrowserRouter>
      <Auth0Provider
        domain={env.AUTH0_DOMAIN!}
        clientId={env.AUTH0_CLIENT_ID!}
        onRedirectCallback={() => {
          resetAppData();
          resetBookingProgress();
        }}
        authorizationParams={{
          redirect_uri: env.AUTH0_CALLBACK_URL,
          audience: env.AUTH0_AUDIENCE,
        }}
      >
        <Routes>
          <Route path="/" element={<ProtectedRoute page={DefaultPage} />}>
            <Route path="" element={<Navigate to="/dashboard" />} />,
            <Route path="/dashboard" element={<ProtectedRoute page={DashboardPage} />} />,
            <Route path="/tasks/" element={<Outlet />}>
              <Route path="callbacks" element={<ProtectedRoute page={CallbacksPage} />} />
              <Route path="tickets" element={<ProtectedRoute page={TicketsPage} />} />
            </Route>
            <Route path="/booking/" element={<BookingFlow />}>
              <Route path="start" element={<ProtectedRoute page={StartPage} />} />
              <Route path="location" element={<ProtectedRoute page={BookingLocationPage} />} />
              <Route path="technician" element={<ProtectedRoute page={BookingTechnicianPage} />} />
              <Route path="payment" element={<ProtectedRoute page={BookingPaymentPage} />} />
              <Route path="confirmation/:id" element={<ProtectedRoute page={BookingConfirmationPage} />} />
              <Route path="*" element={<Navigate to="/booking/start" />} />,
            </Route>
            <Route path="/job/" element={<Outlet />}>
              <Route path="search" element={<ProtectedRoute page={JobSearchPage} />} />
              <Route path="reschedule/:id/:step?" element={<ProtectedRoute page={JobReschedulePage} />} />
              <Route path="cancel/:id" element={<ProtectedRoute page={JobCancelPage} />} />
              <Route path="edit/:id/:tab?" element={<ProtectedRoute page={JobEditPage} />} />
              <Route path="*" element={<Navigate to="/job/search" />} />,
            </Route>
            <Route path="/customer/" element={<Outlet />}>
              <Route path="search" element={<ProtectedRoute page={CustomerSearchPage} />} />
              <Route path="create" element={<ProtectedRoute page={CustomerCreatePage} />} />
              <Route path="edit/:id/:tab?" element={<ProtectedRoute page={CustomerEditPage} />} />
              <Route path="create-parts-only-job/:id" element={<ProtectedRoute page={PartsOnlyJobPage} />} />
              <Route path="*" element={<Navigate to="/customer/search" />} />
            </Route>
            <Route path="/technician/" element={<Outlet />}>
              <Route path="search" element={<ProtectedRoute page={TechnicianSearchPage} />} />
              <Route path="edit/:id/:tab?" element={<ProtectedRoute page={TechnicianEditPage} />} />
              <Route path="edit/:id/availability/away-status" element={<ProtectedRoute page={TechnicianAwayStatusPage} />} />
              <Route path="edit/:id/availability/recurring-availability" element={<ProtectedRoute page={TechnicianRecurringAvailabilityPage} />} />
              <Route path="*" element={<Navigate to="/technician/search" />} />
            </Route>
          </Route>
          <Route path="/logout" element={null} />,
        </Routes>
      </Auth0Provider>
    </BrowserRouter>
  );
};

export default App;
